import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { currentLocale } from '../../i18n';
import { API_URL } from '../../utils/api';
import './AdminPermissions.css';

const PUBLIC_ROLE_ID = '*';

/**
 * Admin panel: command-centric permissions view.
 *
 * Groups commands into three buckets (public / configured / owner-only),
 * lets admins add or remove Discord roles per command, and toggle
 * public access via the ``*`` role.
 */
export default function AdminPermissions({ perms, hideTitle = false }) {
  const { t } = useTranslation();
  const { getAuthHeaders } = useAuth();
  const [data, setData] = useState(null);
  const [roles, setRoles] = useState([]);
  const [webRoles, setWebRoles] = useState([]);  // Sprint 4: voice-platform-agnostiska roller
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null);

  const fetchData = useCallback(async () => {
    if (!perms.guildId) return;
    setLoading(true);
    setError('');
    try {
      const [permRes, rolesRes, webRolesRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/permissions/${perms.guildId}`, {
          headers: getAuthHeaders(),
        }),
        fetch(`${API_URL}/api/admin/permissions/${perms.guildId}/discord-roles`, {
          headers: getAuthHeaders(),
        }),
        fetch(`${API_URL}/api/admin/web-roles?guild_id=${perms.guildId}`, {
          headers: getAuthHeaders(),
        }),
      ]);
      if (!permRes.ok) throw new Error(t('admin.loadPermsFailed'));
      setData(await permRes.json());
      setRoles(rolesRes.ok ? await rolesRes.json() : []);
      if (webRolesRes.ok) {
        const wr = await webRolesRes.json();
        setWebRoles(wr.roles || []);
      } else {
        setWebRoles([]);
      }
    } catch (e) {
      setError(e.message || t('admin.loadError'));
    } finally {
      setLoading(false);
    }
  }, [perms.guildId, getAuthHeaders, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const grouped = useMemo(() => {
    if (!data) return { public: [], configured: [], ownerOnly: [] };
    const pub = [];
    const configured = [];
    const ownerOnly = [];
    Object.entries(data).forEach(([command, info]) => {
      const entry = { command, label: info.label, roles: info.roles };
      if (entry.roles.length === 0) {
        ownerOnly.push(entry);
      } else if (entry.roles.some((r) => r.discord_role_id === PUBLIC_ROLE_ID)) {
        pub.push(entry);
      } else {
        configured.push(entry);
      }
    });
    const byLabel = (a, b) => a.label.localeCompare(b.label, currentLocale());
    return {
      public: pub.sort(byLabel),
      configured: configured.sort(byLabel),
      ownerOnly: ownerOnly.sort(byLabel),
    };
  }, [data]);

  const openEdit = (command, label) => {
    setEditing({ command, label });
  };

  const closeEdit = () => setEditing(null);

  const mutate = async (path, body) => {
    const res = await fetch(`${API_URL}/api/admin/permissions/${perms.guildId}/${path}`, {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      throw new Error(d.detail?.error || d.error || t('admin.operationFailed'));
    }
    return res.json();
  };

  const handleAddRole = async (command, roleId, roleName) => {
    await mutate('add', {
      command,
      discord_role_id: roleId,
      discord_role_name: roleName || '',
    });
    await fetchData();
  };

  /**
   * Sprint 4: koppla en WebRole till ett kommando istället för Discord-roll.
   * Skickar web_role_id (Django CommandPermission.web_role FK).
   */
  const handleAddWebRole = async (command, webRoleId) => {
    await mutate('add', { command, web_role_id: webRoleId });
    await fetchData();
  };

  const handleRemoveRole = async (permId) => {
    await mutate('remove', { id: permId });
    await fetchData();
  };

  if (loading) return <div className="admin-info">{t('admin.loadingPerms')}</div>;
  if (error) return <div className="admin-error">{error}</div>;

  return (
    <div>
      {!hideTitle && <h1 className="admin-section-title">{t('admin.subPermissions')}</h1>}

      <div className="admin-card">
        <div className="admin-card-title">{t('admin.publicCommands')}</div>
        <div className="admin-card-desc">
          {t('admin.publicCommandsDesc', { role: PUBLIC_ROLE_ID })}
        </div>
        <CommandList
          commands={grouped.public}
          onEdit={openEdit}
          emptyText={t('admin.noPublicCommands')}
        />
      </div>

      <div className="admin-card">
        <div className="admin-card-title">{t('admin.roleBased')}</div>
        <div className="admin-card-desc">
          {t('admin.roleBasedDesc')}
        </div>
        <CommandList
          commands={grouped.configured}
          onEdit={openEdit}
          emptyText={t('admin.noRoleCommands')}
        />
      </div>

      <div className="admin-card">
        <div className="admin-card-title">{t('admin.ownerOnly')}</div>
        <div className="admin-card-desc">
          {t('admin.ownerOnlyDesc')}
        </div>
        <CommandList
          commands={grouped.ownerOnly}
          onEdit={openEdit}
          emptyText={t('admin.allCommandsHaveRoles')}
        />
      </div>

      {editing && (
        <EditRolesModal
          entry={data[editing.command]}
          label={editing.label}
          command={editing.command}
          allRoles={roles}
          allWebRoles={webRoles}
          onAdd={handleAddRole}
          onAddWebRole={handleAddWebRole}
          onRemove={handleRemoveRole}
          onClose={closeEdit}
        />
      )}
    </div>
  );
}

/**
 * Flat list of commands inside a group card.
 * Each row shows label + current role chips + edit button.
 */
function CommandList({ commands, onEdit, emptyText }) {
  const { t } = useTranslation();
  if (!commands.length) {
    return <div className="admin-perm-empty">{emptyText}</div>;
  }
  return (
    <div className="admin-perm-list">
      {commands.map((c) => (
        <div className="admin-perm-row" key={c.command}>
          <div className="admin-perm-row-main">
            <div className="admin-perm-label">{c.label}</div>
            <div className="admin-perm-command">{c.command}</div>
          </div>
          <div className="admin-perm-row-chips">
            {c.roles.map((r) => {
              const isWeb = r.role_type === 'web';
              const isPub = r.discord_role_id === PUBLIC_ROLE_ID;
              const cls = isPub
                ? 'admin-perm-chip admin-perm-chip-public'
                : isWeb
                  ? 'admin-perm-chip admin-perm-chip-web'
                  : 'admin-perm-chip';
              const label = isPub
                ? t('admin.publicLabel')
                : isWeb
                  ? `🌐 ${r.web_role_name}`
                  : (r.discord_role_name || r.discord_role_id);
              return <span className={cls} key={r.id}>{label}</span>;
            })}
          </div>
          <button
            className="admin-btn admin-btn-secondary admin-perm-edit-btn"
            onClick={() => onEdit(c.command, c.label)}
          >
            {t('admin.edit')}
          </button>
        </div>
      ))}
    </div>
  );
}

/**
 * Modal for editing which roles a single command is bound to.
 * Supports: public toggle, role-picker dropdown, manual role-ID fallback, remove.
 */
function EditRolesModal({ entry, label, command, allRoles, allWebRoles, onAdd, onAddWebRole, onRemove, onClose }) {
  const { t } = useTranslation();
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [selectedWebRoleId, setSelectedWebRoleId] = useState('');
  const [manualId, setManualId] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  // Discord-rollerna som redan är kopplade till kommandot
  const currentDiscordRoleIds = useMemo(
    () => new Set(entry.roles.filter((r) => r.role_type !== 'web').map((r) => r.discord_role_id)),
    [entry.roles],
  );
  // WebRoles som redan är kopplade
  const currentWebRoleIds = useMemo(
    () => new Set(entry.roles.filter((r) => r.role_type === 'web').map((r) => r.web_role_id)),
    [entry.roles],
  );

  const isPublic = currentDiscordRoleIds.has(PUBLIC_ROLE_ID);

  const availableRoles = useMemo(
    () => allRoles.filter((r) => !currentDiscordRoleIds.has(r.id)),
    [allRoles, currentDiscordRoleIds],
  );
  const availableWebRoles = useMemo(
    () => (allWebRoles || []).filter((r) => !currentWebRoleIds.has(r.id)),
    [allWebRoles, currentWebRoleIds],
  );

  const handleAction = async (fn) => {
    setBusy(true);
    setErr('');
    try {
      await fn();
    } catch (e) {
      setErr(e.message || t('admin.errorGeneric'));
    } finally {
      setBusy(false);
    }
  };

  const handleAddFromDropdown = () => {
    if (!selectedRoleId) return;
    const role = allRoles.find((r) => r.id === selectedRoleId);
    handleAction(async () => {
      await onAdd(command, selectedRoleId, role?.name || '');
      setSelectedRoleId('');
    });
  };

  const handleAddManual = () => {
    const id = manualId.trim();
    if (!id) return;
    handleAction(async () => {
      await onAdd(command, id, '');
      setManualId('');
    });
  };

  const handleAddWebRoleFromDropdown = () => {
    if (!selectedWebRoleId) return;
    handleAction(async () => {
      await onAddWebRole(command, selectedWebRoleId);
      setSelectedWebRoleId('');
    });
  };

  const handleTogglePublic = () => {
    if (isPublic) {
      const publicEntry = entry.roles.find((r) => r.discord_role_id === PUBLIC_ROLE_ID);
      if (publicEntry) {
        handleAction(() => onRemove(publicEntry.id));
      }
    } else {
      handleAction(() => onAdd(command, PUBLIC_ROLE_ID, 'Alla'));
    }
  };

  return (
    <div className="admin-modal-overlay" onClick={onClose}>
      <div
        className="admin-modal admin-perm-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="admin-perm-modal-title">{label}</h3>
        <div className="admin-perm-modal-sub">{command}</div>

        <div className="admin-perm-modal-section">
          <label className="admin-perm-modal-toggle">
            <input
              type="checkbox"
              checked={isPublic}
              onChange={handleTogglePublic}
              disabled={busy}
            />
            <span>{t('admin.makePublic')}</span>
          </label>
        </div>

        <div className="admin-perm-modal-section">
          <div className="admin-perm-modal-heading">{t('admin.activeRoles')}</div>
          {entry.roles.length === 0 && (
            <div className="admin-perm-empty">{t('admin.noRolesSet')}</div>
          )}
          <div className="admin-perm-modal-chips">
            {entry.roles.map((r) => {
              const isWeb = r.role_type === 'web';
              const isPub = r.discord_role_id === PUBLIC_ROLE_ID;
              const cls = isPub
                ? 'admin-perm-chip admin-perm-chip-public'
                : isWeb
                  ? 'admin-perm-chip admin-perm-chip-web'
                  : 'admin-perm-chip';
              const label = isPub
                ? t('admin.publicLabel')
                : isWeb
                  ? `🌐 ${r.web_role_name}`
                  : (r.discord_role_name || r.discord_role_id);
              return (
                <span className={cls} key={r.id}>
                  {label}
                  <button
                    className="admin-perm-chip-remove"
                    onClick={() => handleAction(() => onRemove(r.id))}
                    disabled={busy}
                    title={t('admin.remove')}
                  >
                    ×
                  </button>
                </span>
              );
            })}
          </div>
        </div>

        <div className="admin-perm-modal-section">
          <div className="admin-perm-modal-heading">{t('admin.addDiscordRole')}</div>
          {availableRoles.length > 0 ? (
            <div className="admin-perm-modal-row">
              <select
                className="admin-input admin-perm-select"
                value={selectedRoleId}
                onChange={(e) => setSelectedRoleId(e.target.value)}
                disabled={busy}
              >
                <option value="">{t('admin.selectRole')}</option>
                {availableRoles.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </select>
              <button
                className="admin-btn"
                onClick={handleAddFromDropdown}
                disabled={busy || !selectedRoleId}
              >
                {t('admin.add')}
              </button>
            </div>
          ) : (
            <div className="admin-perm-empty">{t('admin.allKnownRolesAdded')}</div>
          )}

          <div className="admin-perm-modal-row admin-perm-modal-manual">
            <input
              className="admin-input admin-perm-manual-input"
              placeholder={t('admin.manualRoleId')}
              value={manualId}
              onChange={(e) => setManualId(e.target.value)}
              disabled={busy}
            />
            <button
              className="admin-btn admin-btn-secondary"
              onClick={handleAddManual}
              disabled={busy || !manualId.trim()}
            >
              {t('admin.addId')}
            </button>
          </div>
        </div>

        <div className="admin-perm-modal-section">
          <div className="admin-perm-modal-heading">
            🌐 {t('admin.addWebRole')}
          </div>
          <div className="admin-perm-modal-help">
            {t('admin.webRoleHelp')}
          </div>
          {availableWebRoles.length > 0 ? (
            <div className="admin-perm-modal-row">
              <select
                className="admin-input admin-perm-select"
                value={selectedWebRoleId}
                onChange={(e) => setSelectedWebRoleId(e.target.value)}
                disabled={busy}
              >
                <option value="">{t('admin.selectWebRole')}</option>
                {availableWebRoles.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name} ({r.member_count} {t('admin.memberWordMany')})
                  </option>
                ))}
              </select>
              <button
                className="admin-btn"
                onClick={handleAddWebRoleFromDropdown}
                disabled={busy || !selectedWebRoleId}
              >
                {t('admin.add')}
              </button>
            </div>
          ) : (
            <div className="admin-perm-empty">
              {allWebRoles && allWebRoles.length === 0
                ? t('admin.noWebRolesCreated')
                : t('admin.allWebRolesAdded')}
            </div>
          )}
        </div>

        {err && <div className="admin-error">{err}</div>}

        <div className="admin-confirm-actions">
          <button className="admin-btn admin-btn-secondary" onClick={onClose} disabled={busy}>
            {t('profile.close')}
          </button>
        </div>
      </div>
    </div>
  );
}
